import React, { useState, useEffect } from 'react';
import API_URL from '../../config/api';

const Products = () => {
  const [showForm, setShowForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    nomeProduto: '',
    ncm: '',
    unidade: '',
    valorUnitario: '',
    categoria: '',
    descricao: '',
    status: 'ativo',
    ipi: '0'
  });

  // Load products from Google Sheets
  useEffect(() => {
    loadProducts();
  }, []);

  // Filter products based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.nomeProduto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.ncm.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.categoria && product.categoria.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.descricao && product.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [products, searchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/products`);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'valorUnitario') {
      // Allow only digits, comma, and dot, but do not format on every keystroke to allow smooth input
      const regex = /^[\d.,]*$/;
      if (!regex.test(value)) {
        // Ignore invalid input
        return;
      }
      // Just set the raw value without formatting to allow multiple digits input
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'ipi') {
      // Allow only numbers for IPI percentage
      const regex = /^[\d.,]*$/;
      if (!regex.test(value)) {
        return;
      }
      // Limit to 100
      const numValue = parseFloat(value.replace(',', '.'));
      if (!isNaN(numValue) && numValue > 100) {
        return;
      }
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.code || !formData.nomeProduto || !formData.ncm || !formData.unidade || !formData.valorUnitario) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      const url = editingProductId
        ? `${API_URL}/api/products/${editingProductId}`
        : `${API_URL}/api/products`;

      const response = await fetch(url, {
        method: editingProductId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to create product');
      }

      const result = await response.json();

      if (result.success) {
        alert(editingProductId ? 'Produto atualizado com sucesso!' : 'Produto cadastrado com sucesso!');
        setFormData({
          code: '',
          nomeProduto: '',
          ncm: '',
          unidade: '',
          valorUnitario: '',
          categoria: '',
          descricao: '',
          status: 'ativo',
          ipi: '0'
        });
        setShowForm(false);
        setEditingProductId(null);
        loadProducts(); // Reload products list
      } else {
        alert(result.message || (editingProductId ? 'Erro ao atualizar produto' : 'Erro ao cadastrar produto'));
      }
    } catch (err) {
      console.error('Error creating product:', err);
      alert('Erro ao cadastrar produto: ' + err.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      const result = await response.json();
      
      if (result.success) {
        alert('Produto excluído com sucesso!');
        loadProducts(); // Reload products list
      } else {
        alert(result.message || 'Erro ao excluir produto');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Erro ao excluir produto: ' + err.message);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <h3 className="font-bold">Erro ao carregar produtos</h3>
        <p>{error}</p>
        <button 
          onClick={loadProducts}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-2">
        <h2 className="text-xl sm:text-2xl font-semibold">Cadastro de Produtos</h2>
        <button
          onClick={() => {
            setEditingProductId(null);
            setFormData({
              code: '',
              nomeProduto: '',
              ncm: '',
              unidade: '',
              valorUnitario: '',
              categoria: '',
              descricao: '',
              status: 'ativo',
              ipi: '0'
            });
            setShowForm(true);
          }}
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-blue-700 text-sm sm:text-base w-full sm:w-auto"
        >
          Novo Produto
        </button>
      </div>

      {showForm && (
        <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-medium mb-4">{editingProductId ? 'Editar Produto' : 'Novo Produto'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Código *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Código do produto"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nome do Produto *
                </label>
                <input
                  type="text"
                  name="nomeProduto"
                  value={formData.nomeProduto}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Nome do produto"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  NCM *
                </label>
                <input
                  type="text"
                  name="ncm"
                  value={formData.ncm}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Código NCM"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Unidade *
                </label>
                <select
                  name="unidade"
                  value={formData.unidade}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione uma unidade</option>
                  <option value="UN">Unidade (UN)</option>
                  <option value="CT">Cento (CT)</option>
                  <option value="M2">Metro Quadrado (M²)</option>
                  <option value="M">Metro (M)</option>
                  <option value="KG">Quilograma (KG)</option>
                  <option value="PC">Peça (PC)</option>
                  <option value="CX">Caixa (CX)</option>
                  <option value="L">Litro (L)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Valor Unitário *
                </label>
                <input
                  type="text"
                  name="valorUnitario"
                  value={formData.valorUnitario}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="R$ 0,00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  % IPI
                </label>
                <input
                  type="text"
                  name="ipi"
                  value={formData.ipi}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">Percentual de IPI (0 a 100)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Categoria
                </label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Isotelha">Isotelha</option>
                  <option value="Colonial">Colonial</option>
                  <option value="Telha Simples">Telha Simples</option>
                  <option value="Telhas EPS">Telhas EPS</option>
                  <option value="Acessórios">Acessórios</option>
                  <option value="BenchMark">BenchMark</option>
                  <option value="Isolamentos">Isolamentos</option>
                  <option value="Coberturas">Coberturas</option>
                  <option value="Painéis">Painéis</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Descrição
                </label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Descrição do produto"
                ></textarea>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button 
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Pagination Controls - Top */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Buscar:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por código, nome, NCM, categoria..."
              className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
            />
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Itens por página</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
        <span className="text-sm text-gray-600">
          {filteredProducts.length === 0 ? '0-0 de 0' : `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, filteredProducts.length)} de ${filteredProducts.length}`}
        </span>
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="block xl:hidden space-y-4">
        {filteredProducts.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
            {searchTerm ? 'Nenhum produto encontrado para a busca' : 'Nenhum produto cadastrado'}
          </div>
        ) : (
          filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((product) => (
            <div key={product.id} className="bg-white shadow rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{product.nomeProduto}</h3>
                  <p className="text-sm text-gray-600">Código: {product.code}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  product.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {product.status === 'ativo' ? 'Ativo' : 'Inativo'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <span className="font-medium text-gray-700">NCM:</span>
                  <p className="text-gray-900">{product.ncm}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Unidade:</span>
                  <p className="text-gray-900">{product.unidade}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Valor:</span>
                  <p className="text-gray-900">{formatCurrency(product.valorUnitario)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">IPI:</span>
                  <p className="text-gray-900">{product.ipi || 0}%</p>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Categoria:</span>
                  <p className="text-gray-900">{product.categoria || '-'}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setFormData({
                      code: product.code,
                      nomeProduto: product.nomeProduto,
                      ncm: product.ncm,
                      unidade: product.unidade,
                      valorUnitario: product.valorUnitario,
                      categoria: product.categoria || '',
                      descricao: product.descricao || '',
                      status: product.status || 'ativo',
                      ipi: product.ipi || '0'
                    });
                    setEditingProductId(product.id);
                    setShowForm(true);
                  }}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="flex-1 bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                >
                  Excluir
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden xl:block bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Código
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome do Produto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                NCM
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unidade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor Unitário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                % IPI
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoria
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-2 sm:px-4 lg:px-6 py-4 text-center text-gray-500">
                  Nenhum produto cadastrado
                </td>
              </tr>
            ) : (
              filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((product) => (
                <tr key={product.id}>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.code}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.nomeProduto}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.ncm}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.unidade}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(product.valorUnitario)}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.ipi || 0}%
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.categoria || '-'}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </td>
                  <td className="px-2 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm space-x-2 sm:space-x-4">
                    <button
                      onClick={() => {
                        setFormData({
                          code: product.code,
                          nomeProduto: product.nomeProduto,
                          ncm: product.ncm,
                          unidade: product.unidade,
                          valorUnitario: product.valorUnitario,
                          categoria: product.categoria || '',
                          descricao: product.descricao || '',
                          status: product.status || 'ativo',
                          ipi: product.ipi || '0'
                        });
                        setEditingProductId(product.id);
                        setShowForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls - Bottom */}
      {filteredProducts.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-2">
          <div className="text-sm text-gray-600">
            Página {currentPage} de {Math.ceil(filteredProducts.length / itemsPerPage)}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredProducts.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
